import { useMemo } from 'react';
import { LoadingState } from 'components/ui';
import { PrismThemeProvider } from 'theme';
import { OtpResult, OtpSetup, OtpVerification } from './components';
import { useOtpFlow } from './hooks';
import { OTP_COMPLETING_LABEL, OTP_LOADING_LABEL } from './OTP.constants';
import type { OTPProps } from './OTP.props';

const OTP = (props: OTPProps) => {
  const { showActionButtons = true, showAuthHeader, showMandatoryHint = false } = props;
  const {
    backupCodes,
    handleBackupCodesAcknowledge,
    currentStep,
    formState,
    generateBackupCodes,
    hasBackupCodes,
    handleCancelSetup,
    handleCopyToClipboard,
    handleInputBlur,
    handleInputChange,
    handleSubmit,
    handleToggleOtpKey,
    isCodeWorking,
    isCompleting,
    isGeneratingBackupCodes,
    isSubmitting,
    logOutUser,
    messageBoxData,
    otpData,
    otpSecretKey,
    rememberThisComputer,
    returnToOtpVerificationFlow,
    showRememberThisComputer,
    showOtpKey,
    startBackupCodeFlow,
    setRememberThisComputer,
    username,
  } = useOtpFlow(props);

  const content = useMemo(() => {
    if (isCompleting) {
      return <LoadingState label={OTP_COMPLETING_LABEL} />;
    }

    if (currentStep === 'verify') {
      return (
        <OtpVerification
          formState={formState}
          isCodeWorking={isCodeWorking}
          isSubmitting={isSubmitting}
          rememberThisComputer={rememberThisComputer}
          showMandatoryHint={showMandatoryHint}
          showRememberThisComputer={showRememberThisComputer}
          showHeader={showAuthHeader}
          showBackupCodeAction={hasBackupCodes}
          onCancel={isCodeWorking ? returnToOtpVerificationFlow : logOutUser}
          onCodeNotWorking={startBackupCodeFlow}
          onInputBlur={handleInputBlur}
          onInputChange={handleInputChange}
          onRememberThisComputerChange={setRememberThisComputer}
          onSubmit={handleSubmit}
        />
      );
    }

    if (currentStep === 'setup' && otpData) {
      return (
        <OtpSetup
          formState={formState}
          otpSecretKey={otpSecretKey}
          otpUrl={otpData.url}
          showHeader={showAuthHeader}
          isSubmitting={isSubmitting}
          showMandatoryHint={showMandatoryHint}
          showOtpKey={showOtpKey}
          onCancel={handleCancelSetup}
          onCopyOtpKey={handleCopyToClipboard}
          onInputBlur={handleInputBlur}
          onInputChange={handleInputChange}
          onSubmit={handleSubmit}
          onToggleOtpKey={handleToggleOtpKey}
        />
      );
    }

    if (currentStep === 'result') {
      return (
        <OtpResult
          backupCodes={backupCodes}
          isGeneratingBackupCodes={isGeneratingBackupCodes}
          messageBoxData={messageBoxData}
          onBackupCodesAcknowledge={handleBackupCodesAcknowledge}
          onGenerateBackupCodes={generateBackupCodes}
          showActionButtons={showActionButtons}
          showHeader={showAuthHeader}
          username={username}
        />
      );
    }

    return <LoadingState label={OTP_LOADING_LABEL} />;
  }, [
    backupCodes,
    currentStep,
    formState,
    generateBackupCodes,
    handleBackupCodesAcknowledge,
    handleCancelSetup,
    handleCopyToClipboard,
    handleInputBlur,
    handleInputChange,
    handleSubmit,
    handleToggleOtpKey,
    hasBackupCodes,
    isCodeWorking,
    isCompleting,
    isGeneratingBackupCodes,
    isSubmitting,
    logOutUser,
    messageBoxData,
    otpData,
    otpSecretKey,
    rememberThisComputer,
    returnToOtpVerificationFlow,
    setRememberThisComputer,
    showActionButtons,
    showAuthHeader,
    showMandatoryHint,
    showOtpKey,
    showRememberThisComputer,
    startBackupCodeFlow,
    username,
  ]);

  return <PrismThemeProvider>{content}</PrismThemeProvider>;
};

export default OTP;
